import { useState, useEffect } from 'react';
import { getUser } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { SIGN_UP_URL } from '../constants';

const BANNER_STORAGE_KEY = 'schedule-banner-operatory';
const CALLOUT_STORAGE_KEY = 'schedule-callout-operatory';

type OperatoryAvailabilityVariant = 'banner' | 'callout';
const PREFIX = 'schedule-calendar';

const VARIANT_CONFIG = {
  banner: {
    storageKey: BANNER_STORAGE_KEY,
    dismissEventName: `${PREFIX}:operatory-view:availability-banner:dismiss-button`,
    signUpEventName: `${PREFIX}:operatory-view:availability-banner:sign-up-button`,
  },
  callout: {
    storageKey: CALLOUT_STORAGE_KEY,
    dismissEventName: `${PREFIX}:operatory-view:availability-callout:dismiss-button`,
    signUpEventName: `${PREFIX}:operatory-view:availability-callout:sign-up-button`,
  },
};

const getStorageKey = (baseKey: string, userId: string, locationIds: string[]) => {
  return `${baseKey}-${userId}-${[...locationIds].sort().join('-')}`;
};

export const useOperatoryAvailability = (variant: OperatoryAvailabilityVariant) => {
  const [isDismissed, setIsDismissed] = useState(true); // default to dismissed until we check localStorage
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0] || 'unknown-location';
  const userId = getUser()?.userID || 'unknown-user';

  const config = VARIANT_CONFIG[variant];

  useEffect(() => {
    const storageKey = getStorageKey(config.storageKey, userId, selectedLocationIds);
    const stored = localStorage.getItem(storageKey);
    setIsDismissed(stored === 'true');
  }, [config.storageKey, userId, selectedLocationIds]);

  const dismiss = () => {
    const storageKey = getStorageKey(config.storageKey, userId, selectedLocationIds);
    localStorage.setItem(storageKey, 'true');
    setIsDismissed(true);

    pendo.track(config.dismissEventName, {
      locationId,
      userId,
    });
  };

  const signUp = () => {
    window.open(SIGN_UP_URL, '_blank', 'noopener,noreferrer');

    pendo.track(config.signUpEventName, {
      locationId,
      userId,
    });
  };

  const shouldShow = !isDismissed;

  return {
    isDismissed,
    dismiss,
    signUp,
    shouldShow,
  };
};

export const useOperatoryAvailabilityBanner = () => {
  const banner = useOperatoryAvailability('banner');
  return {
    isBannerDismissed: banner.isDismissed,
    dismissBanner: banner.dismiss,
    signUpBanner: banner.signUp,
    shouldShowBanner: banner.shouldShow,
  };
};

export const useOperatoryAvailabilityCallout = () => {
  const callout = useOperatoryAvailability('callout');
  return {
    isCalloutDismissed: callout.isDismissed,
    dismissCallout: callout.dismiss,
    signUpCallout: callout.signUp,
    shouldShowCallout: callout.shouldShow,
  };
};
